import { auth } from '../firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export interface GHLConfig {
  connected: boolean;
  location_id: string | null;
  location_name: string | null;
  webhook_url: string | null;
  connected_at: string | null;
  last_sync: { completed_at: string; contacts_synced: number } | null;
}

export interface SyncStatus {
  status: 'idle' | 'running' | 'completed' | 'failed';
  progress: {
    total_contacts: number;
    processed: number;
    created: number;
    updated: number;
    skipped: number;
    percent: number;
  };
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  last_sync: { completed_at: string; contacts_synced: number } | null;
}

export async function getGHLConfig(tenantId: string): Promise<GHLConfig> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/ghl/config`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to get GHL config: ${res.statusText}`);
  }
  return res.json();
}

export async function connectGHL(
  tenantId: string,
  locationId: string,
  pitToken: string,
): Promise<{ success: boolean; location_name: string; webhook_url: string }> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/ghl/connect`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ location_id: locationId, pit_token: pitToken }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to connect GHL: ${res.statusText}`);
  }
  return res.json();
}

export async function disconnectGHL(tenantId: string): Promise<void> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/ghl/disconnect`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to disconnect GHL: ${res.statusText}`);
  }
}

export async function triggerGHLSync(
  tenantId: string,
): Promise<{ sync_job_id: string; tenant_id: string; message: string }> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/ghl/sync`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to trigger sync: ${res.statusText}`);
  }
  return res.json();
}

export async function getGHLSyncStatus(tenantId: string): Promise<SyncStatus> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/ghl/sync/status`, {
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to get sync status: ${res.statusText}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Meta / Facebook self-serve "Connect Facebook" OAuth
// ---------------------------------------------------------------------------
//
// Wraps backend/api/meta_connect.py (the tenant connects its OWN Facebook Page to
// the shared platform Meta app). The tenant is derived from the authenticated
// caller server-side; the frontend never passes a raw tenant param. These
// endpoints return 503 until the platform META_APP_ID / secret are configured.

/**
 * The OAuth redirect_uri the backend uses (must match META_OAUTH_REDIRECT_URI on
 * the platform). The frontend's callback page lives at /integrations/meta/callback;
 * the origin is configurable via VITE_META_REDIRECT_ORIGIN (falls back to the
 * current browser origin) so dev + prod both work.
 */
export function getMetaRedirectUri(): string {
  const origin =
    (import.meta.env.VITE_META_REDIRECT_ORIGIN as string | undefined)?.trim() ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  return `${origin}/integrations/meta/callback`;
}

/** Raised when the backend returns 503 (the platform Meta app is not configured yet). */
export class MetaNotConfiguredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MetaNotConfiguredError';
  }
}

export interface MetaStatus {
  connected: boolean;
  page_id: string | null;
  page_name: string | null;
  connected_at: string | null;
}

export interface MetaConnectUrl {
  connect_url: string;
  state: string;
  scopes: string[];
  redirect_uri: string;
}

export interface MetaConnectResult {
  success: boolean;
  page_id: string;
  page_name: string;
  subscribed: boolean;
}

async function parseMetaError(res: Response): Promise<never> {
  const body = await res.json().catch(() => ({ detail: res.statusText }));
  // FastAPI detail can be a string or an object ({ error, marker }).
  const detail = body?.detail;
  const message =
    typeof detail === 'string'
      ? detail
      : detail?.error || JSON.stringify(detail) || res.statusText;
  if (res.status === 503) {
    throw new MetaNotConfiguredError(message || 'Meta app not configured');
  }
  throw new Error(message || `Request failed: ${res.statusText}`);
}

/** Whether the caller's tenant has a connected Meta page. */
export async function getMetaStatus(): Promise<MetaStatus> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/integrations/meta/status`, { headers });
  if (!res.ok) await parseMetaError(res);
  return res.json();
}

/**
 * Get the Facebook Login dialog URL for the caller's tenant. The frontend then
 * redirects the browser to `connect_url`; Meta redirects back to redirect_uri with
 * ?code=...&state=... Passes our own redirect_uri so connect-url + connect match.
 */
export async function getMetaConnectUrl(): Promise<MetaConnectUrl> {
  const headers = await getAuthHeader();
  const redirectUri = encodeURIComponent(getMetaRedirectUri());
  const res = await fetch(
    `${API_BASE_URL}/api/integrations/meta/connect-url?redirect_uri=${redirectUri}`,
    { headers },
  );
  if (!res.ok) await parseMetaError(res);
  return res.json();
}

/**
 * Complete the OAuth flow: exchange `code` for a long-lived PAGE token for the
 * selected page, store it per-tenant, and subscribe the page webhook. The same
 * redirect_uri used at connect-url MUST be sent (Graph requires an exact match).
 */
export async function connectMeta(params: {
  code: string;
  state: string;
  page_id: string;
}): Promise<MetaConnectResult> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/integrations/meta/connect`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...params, redirect_uri: getMetaRedirectUri() }),
  });
  if (!res.ok) await parseMetaError(res);
  return res.json();
}
